// apps/roleforge-app/src/pages/CoursePage.tsx
//
// RoleForge v2 Plan 2, Task 3 — the DIY Implementation Course.
//
// Entitlement-gated: not entitled → redirect to the delivery page /audits
// (which shows the re-offer card). While the entitlement check is in flight
// nothing redirects — a slow response must not flash the customer away from
// a course they own. A FAILED check renders a calm retry state, never a
// redirect: per the Task 2 rule, a fetch failure is "entitlement unknown",
// not "no access".
//
// Tone rule for this file: calm. No urgency, no countdowns, no pressure copy.

import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getCourseEntitlement } from '../lib/api-client';
import { COURSE_MODULES, type CourseModule } from '../lib/course-content';

type EntitlementState = 'loading' | 'entitled' | 'not_entitled' | 'unknown';

// ─── Video rendering by URL shape ───────────────────────────────────────────
//
// YouTube/Vimeo URLs render as an iframe embed; anything else (e.g. a direct
// .mp4 on a CDN) renders as a plain <video>. Owner drops URLs into
// course-content.ts later — no code change needed here.
//
// YouTube refuses to be framed at its watch/share URLs (blank player, no
// error), and those are exactly the shapes an owner will paste — so
// `toEmbedUrl` normalizes watch/youtu.be URLs to the /embed/ form and bare
// vimeo.com/<id> URLs to player.vimeo.com before iframing.

/**
 * Returns the frameable embed URL for a YouTube/Vimeo link, or null when the
 * URL is not a recognized embed provider (callers render a plain <video>).
 */
export function toEmbedUrl(url: string): string | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  const host = u.hostname.replace(/^www\./i, '').toLowerCase();

  if (host === 'youtu.be') {
    const id = u.pathname.split('/').filter(Boolean)[0];
    return id ? `https://www.youtube.com/embed/${id}` : null;
  }
  if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host.endsWith('.youtube.com')) {
    if (u.pathname.startsWith('/embed/')) return url;
    if (u.pathname === '/watch') {
      const id = u.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    // An unrecognized YouTube path can't be safely framed OR played by
    // <video>; treat it as an embed of the page as-is is worse than nothing,
    // so fall through to null and let the plain <video> fail visibly.
    return null;
  }
  if (host === 'player.vimeo.com') return url;
  if (host === 'vimeo.com') {
    const id = /^\/(\d+)/.exec(u.pathname)?.[1];
    return id ? `https://player.vimeo.com/video/${id}` : null;
  }
  return null;
}

function ModuleVideo({ module }: { module: CourseModule }) {
  if (!module.videoUrl) {
    return (
      <p className="text-sm text-graphite" data-testid={`video-coming-soon-${module.id}`}>
        Video coming soon — the written steps below cover everything.
      </p>
    );
  }
  const embedUrl = toEmbedUrl(module.videoUrl);
  if (embedUrl) {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-lg border border-line">
        <iframe
          src={embedUrl}
          title={module.title}
          className="h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <video
      src={module.videoUrl}
      controls
      className="w-full rounded-lg border border-line"
      data-testid={`video-${module.id}`}
    />
  );
}

// ─── Module card ────────────────────────────────────────────────────────────

function ModuleCard({ module, index }: { module: CourseModule; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <section
      data-testid={`course-module-${module.id}`}
      className="rounded-2xl border border-line bg-card shadow-soft"
    >
      {/* Accordion a11y: heading-wrapped toggle + aria-controls/id pairing. */}
      <h2 className="m-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={`course-module-body-${module.id}`}
          data-testid={`course-module-toggle-${module.id}`}
          className="flex w-full items-start justify-between gap-4 p-6 text-left"
        >
          <span className="space-y-1">
            <span className="block text-xs font-semibold uppercase tracking-wide text-graphite">
              Module {index + 1}
            </span>
            <span className="block text-lg font-semibold text-ink">{module.title}</span>
            <span className="block text-sm font-normal text-graphite">{module.description}</span>
          </span>
          <span aria-hidden className="mt-1 text-graphite">{open ? '−' : '+'}</span>
        </button>
      </h2>
      {open && (
        <div
          id={`course-module-body-${module.id}`}
          className="space-y-4 border-t border-line p-6"
          data-testid={`course-module-body-${module.id}`}
        >
          <ModuleVideo module={module} />
          <ol className="list-decimal space-y-2 pl-5">
            {module.steps.map((step, i) => (
              <li key={i} className="text-sm text-graphite">{step}</li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function CoursePage() {
  const { jwt } = useAuth();
  const [state, setState] = useState<EntitlementState>('loading');

  useEffect(() => {
    // RequireAuth (App.tsx) guarantees a session before this page mounts, so
    // jwt is only briefly null while the auth context hydrates.
    if (!jwt) return;
    let cancelled = false;
    void (async () => {
      try {
        const { entitled } = await getCourseEntitlement(jwt);
        if (!cancelled) setState(entitled ? 'entitled' : 'not_entitled');
      } catch {
        if (!cancelled) setState('unknown');
      }
    })();
    return () => { cancelled = true; };
  }, [jwt]);

  if (state === 'loading') {
    return (
      <div className="p-8 text-graphite" data-testid="course-loading">
        Loading your course…
      </div>
    );
  }

  if (state === 'not_entitled') {
    // The delivery page carries the re-offer card.
    return <Navigate to="/audits" replace />;
  }

  if (state === 'unknown') {
    return (
      <div className="space-y-2" data-testid="course-check-failed">
        <h1 className="text-2xl font-semibold text-ink">DIY Implementation Course</h1>
        <p className="text-sm text-graphite">
          We couldn't check your course access just now. Refresh in a moment —
          nothing about your order has changed.
        </p>
        <Link to="/audits" className="text-sm font-semibold text-plum">
          Back to your Blueprint →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-ink">
          DIY Implementation Course
        </h1>
        <p className="max-w-2xl text-graphite">
          Step-by-step lessons for putting your Blueprint to work yourself —
          one tool at a time, at your own pace.
        </p>
        <Link to="/audits" data-testid="course-back-link" className="text-sm font-semibold text-plum">
          ← Back to your Blueprint
        </Link>
      </header>

      <div className="space-y-4">
        {COURSE_MODULES.map((module, i) => (
          <ModuleCard key={module.id} module={module} index={i} />
        ))}
      </div>
    </div>
  );
}
